// weather/use-weather.jsx — the weather model for the panel, live.
//
// Day, observation and warning data are plain entities in hass.states.
// The hourly forecast is not: since HA 2024.3 it is only available on
// request, so this subscribes to `weather/subscribe_forecast`, which
// pushes a fresh list whenever BOM's hourly forecast changes. The
// websocket library re-subscribes on reconnect by itself.
//
// `src` picks the place (model.js LOCATIONS); home when left out. A null
// `src` is "off": no subscription and a null model, so the Weather page
// can hold a second instance for another place without paying for it
// while home is showing.

import * as React from 'react';
import {
  BOM, readCurrent, readDays, readHours, summarise, landWarnings,
} from './model.js';

export function useWeather(hass, now, src = BOM) {
  // undefined until the first answer, null if the subscription failed.
  const [forecast, setForecast] = React.useState(undefined);
  const conn = hass?.connection;
  const hourly = src?.hourly;

  React.useEffect(() => {
    setForecast(undefined);
    if (!hourly || !conn?.subscribeMessage) return undefined;
    let unsub = null;
    let cancelled = false;
    conn.subscribeMessage(
      (msg) => setForecast(Array.isArray(msg?.forecast) ? msg.forecast : null),
      { type: 'weather/subscribe_forecast', entity_id: hourly, forecast_type: 'hourly' },
    ).then((u) => { if (cancelled) u(); else unsub = u; })
     .catch(() => setForecast(null));
    return () => { cancelled = true; if (unsub) unsub(); };
  }, [conn, hourly]);

  const states = hass?.states;
  return React.useMemo(() => {
    if (!src) return null;
    const days = readDays(states, now, src);
    return {
      current: readCurrent(states, now, src),
      days,
      summary: summarise(days, now),
      warnings: landWarnings(states, now, src),
      hours: readHours(forecast ?? null, days, now, 24),
      hoursPending: forecast === undefined && !!conn?.subscribeMessage,
      ready: days.length > 0,
    };
  }, [states, forecast, now, src, conn]);
}
